import React, { useState } from "react";
import { ChevronRight } from "lucide-react";

const ClanManagementSection = () => {
  const [stepIndex, setStepIndex] = useState(0);
  const [expandedSections, setExpandedSections] = useState({
    0: true,
    1: false,
    2: false,
    6: false,
    7: false,
    10: false,
    11: false,
    12: false,
    20: false,
    25: false,
    30: false,
    40: false,
    50: false,
  });

  const steps = [
    {
      title: "Clan Management Hub",
      level: 0,
      image: "/assets/guide/auth/profile/clan/members/members_tab.png",
      description:
        "The central hub for clan administration. Manage your roster, schedule events, configure the game board, and upload custom tasks for your games.",
    },
    { title: "Clan Settings", level: 0, isHeader: true, id: 1 },
    {
      title: "Edit Clan Name",
      level: 1,
      parentId: 1,
      image: "/assets/guide/auth/profile/clan/edit_clan_name_modal.png",
      description:
        "Update your clan's public identity. Use this tool to rename your organization across the platform.",
    },
    {
      title: "Clan Actions",
      level: 1,
      parentId: 1,
      isHeader: true,
      id: 2,
      image: "/assets/guide/auth/profile/clan/clan_management_actions.png",
      description:
        "Access administrative tools and high-level management functions for your clan.",
    },
    {
      title: "Clan Management Actions",
      level: 2,
      parentId: 2,
      image: "/assets/guide/auth/profile/clan/clan_management_actions.png",
      description:
        "Access critical clan actions. Use this menu to perform high-level administrative tasks, such as permanently disbanding the clan.",
    },
    {
      title: "Disband Confirmation",
      level: 2,
      parentId: 2,
      image: "/assets/guide/auth/profile/clan/disband_clan_confirmation.png",
      description:
        "The final safety check. This will permanently delete the clan and all associated event data. This action is irreversible.",
    },
    { title: "Member Management", level: 0, isHeader: true, id: 6 },
    {
      title: "Add Member",
      level: 1,
      parentId: 6,
      image: "/assets/guide/auth/profile/clan/members/add_member_modal.png",
      description:
        "Add new members to your roster. Search for a user by their Discord username and assign them a starting role in the clan.",
    },
    {
      title: "Member Options",
      level: 1,
      parentId: 6,
      isHeader: true,
      id: 7,
      image:
        "/assets/guide/auth/profile/clan/members/add_member_modal_options.png",
      description:
        "Define the specific roles and power levels for your clan members.",
    },
    {
      title: "Role Permissions",
      level: 2,
      parentId: 7,
      image:
        "/assets/guide/auth/profile/clan/members/add_member_modal_options.png",
      description:
        "MManage the clan hierarchy. Assign specific roles to new members to define their level of authority, from event management to full administrative access.",
    },
    {
      title: "Roster View",
      level: 1,
      parentId: 6,
      image:
        "/assets/guide/auth/profile/clan/members/members_tab_with_member.png",
      description:
        "Manage your clan roster. View all current members and perform moderation actions like promoting, demoting, or kicking users to maintain your clan hierarchy.",
    },
    { title: "Event Management", level: 0, isHeader: true, id: 10 },
    {
      title: "Events Hub",
      level: 1,
      parentId: 10,
      image: "/assets/guide/auth/profile/clan/events/events_tab.png",
      description:
        "The central hub for clan events. Owners and admins can schedule new competitions, track upcoming games, and manage the full history of past events.",
    },
    { title: "Manage Events", level: 1, parentId: 10, isHeader: true, id: 12 },
    {
      title: "Create Event",
      level: 2,
      parentId: 12,
      image:
        "/assets/guide/auth/profile/clan/events/create_new_event_modal.png",
      description:
        "Schedule a new event. Enter a name and set the start and end times to add a new game session to your clan calendar.",
    },
    {
      title: "Event List",
      level: 2,
      parentId: 12,
      image:
        "/assets/guide/auth/profile/clan/events/event_list/events_tab_with_event.png",
      description:
        "Manage your event schedule. View all upcoming and ongoing competitions, monitor participation, and quickly jump into team or game settings.",
    },
    {
      title: "Past Events",
      level: 2,
      parentId: 12,
      image:
        "/assets/guide/auth/profile/clan/events/event_list/events_tab_past_event.png",
      description:
        "Clan Hall of Fame. Review the results, team lineups, and outcomes from all your previously completed competitions.",
    },
    { title: "Manage Teams", level: 1, parentId: 10, isHeader: true, id: 11 },
    {
      title: "Manage Teams Overview",
      level: 2,
      parentId: 11,
      image:
        "/assets/guide/auth/profile/clan/events/manage_teams/manage_teams_sub_tab.png",
      description:
        "Manage event teams. Create and organize teams for your event, ensuring they are balanced and ready before the competition begins.",
    },
    {
      title: "Create Team",
      level: 2,
      parentId: 11,
      image:
        "/assets/guide/auth/profile/clan/events/manage_teams/create_new_team_modal.png",
      description:
        "Establish a new team for an upcoming event and prepare it for member assignment.",
    },
    {
      title: "Team Detail",
      level: 2,
      parentId: 11,
      image:
        "/assets/guide/auth/profile/clan/events/manage_teams/manage_teams_sub_tab_with_team.png",
      description:
        "Manage event teams. View all participating teams, modify their details—including names and colors—or adjust rosters and remove teams as needed.",
    },
    {
      title: "Teams Roster",
      level: 2,
      parentId: 11,
      image:
        "/assets/guide/auth/profile/clan/events/manage_teams/manage_teams_sub_tab_with_teams.png",
      description:
        "A high-level overview of all teams competing in the current event.",
    },
    {
      title: "Expand Team View",
      level: 2,
      parentId: 11,
      isHeader: true,
      id: 13,
      image:
        "/assets/guide/auth/profile/clan/events/manage_teams/manage_teams_sub_tab_with_teams_expanded_members.png",
      description:
        "Open the team list to see the individual members assigned to each group.",
    },
    {
      title: "Assign Members",
      level: 3,
      parentId: 13,
      image:
        "/assets/guide/auth/profile/clan/events/manage_teams/manage_teams_sub_tab_with_teams_expanded_members_filled.png",
      description:
        "Populate your teams. Select from your available clan roster to fill out team slots for the competition.",
    },
    {
      title: "Roster Finalized",
      level: 3,
      parentId: 13,
      image:
        "/assets/guide/auth/profile/clan/events/manage_teams/manage_teams_sub_tab_with_teams_expanded_members_filled_members.png",
      description:
        "Confirm your team lineups. Once locked in, these members are ready to represent your clan on the board.",
    },

    // Manage Game Hierarchy
    { title: "Manage Game", level: 1, parentId: 10, isHeader: true, id: 20 },
    {
      title: "Setup Form",
      level: 2,
      parentId: 20,
      image:
        "/assets/guide/auth/profile/clan/events/manage_game/manage_game_setup_form.png",
      description:
        "Configure the technical parameters of the game session, including board size and rule presets.",
    },
    {
      title: "Post Setup View",
      level: 2,
      parentId: 20,
      image:
        "/assets/guide/auth/profile/clan/events/manage_game/manage_game_post_setup.png",
      description:
        "The dashboard view once the game environment is initialized and ready for players.",
    },
    {
      title: "Live Game Tracker",
      level: 2,
      parentId: 20,
      isHeader: true,
      id: 25,
      image:
        "/assets/guide/auth/profile/clan/events/manage_game/manage_game_post_setup.png",
      description: "Track active game progress.",
    },
    {
      title: "Live Game Tracker View",
      level: 3,
      parentId: 25,
      image:
        "/assets/guide/auth/profile/clan/events/manage_game/live_game_tracker_results.png",
      description:
        "Live Game Tracker. Monitor real-time game progress, including team positions and turn counts. Use admin tools to pause or reset the game, manually override team positions, and process reverts without impacting the team's official revert limit.",
    },
    {
      title: "Pause Game",
      level: 3,
      parentId: 25,
      image:
        "/assets/guide/auth/profile/clan/events/manage_game/pause_game_button.png",
      description:
        "Pause the game. Instantly halt all turn processing to resolve disputes, manage board settings, or take a break without losing current game progress.",
    },
    {
      title: "Resume Game",
      level: 3,
      parentId: 25,
      image:
        "/assets/guide/auth/profile/clan/events/manage_game/resume_game_button.png",
      description:
        "Resume game. Unpause the session to re-enable turn processing and allow players to resume rolling on the board.",
    },
    {
      title: "Reset Game Confirmation",
      level: 3,
      parentId: 25,
      image:
        "/assets/guide/auth/profile/clan/events/manage_game/reset_game_confirmation.png",
      description:
        "Reset game. Permanently clear all team positions, move history, and revert counts. This action will reset the game status to 'READY' and cannot be undone.",
    },
    {
      title: "View Live Game",
      level: 3,
      parentId: 25,
      image:
        "/assets/guide/auth/profile/clan/events/manage_game/view_live_game_button_destination.png",
      description:
        "Live game board. Opens the interactive board view, where you can monitor real-time positions, track gameplay logs, and view specific challenges for each tile.",
    },
    {
      title: "Set Team Position",
      level: 3,
      parentId: 25,
      image:
        "/assets/guide/auth/profile/clan/events/manage_game/admin_set_team_position_modal.png",
      description:
        "Admin override tool. Manually move a team to a specific tile to correct positioning errors or resolve disputes.",
    },
    {
      title: "Revert Team Position",
      level: 3,
      parentId: 25,
      image:
        "/assets/guide/auth/profile/clan/events/manage_game/admin_revert_team_position_modal.png",
      description:
        "Admin revert. Roll back the last move made by a team. This bypasses the standard game logic and does not consume any of the team's allotted reverts.",
    },
    {
      title: "Manual Board Config",
      level: 2,
      parentId: 20,
      isHeader: true,
      id: 40,
      description: "Manually configure the board.",
    },
    {
      title: "Manual Board Configuration",
      level: 3,
      parentId: 40,
      image:
        "/assets/guide/auth/profile/clan/events/manage_game/manual_board_configuration.png",
      description:
        "Customize the board layout. Manually place snakes and ladders to build a unique challenge for your clan members.",
    },
    {
      title: "Board Settings",
      level: 2,
      parentId: 20,
      isHeader: true,
      id: 30,
      image:
        "/assets/guide/auth/profile/clan/events/manage_game/manage_game_post_setup.png",
      description: "System board settings.",
    },
    {
      title: "Board Settings View",
      level: 3,
      parentId: 30,
      image:
        "/assets/guide/auth/profile/clan/events/manage_game/board_settings.png",
      description:
        "Configure your game environment. Define the board size, specify the number of hazards (snakes/ladders), and set the revert limit for each team. Once finalized, use 'Save & Randomize Board' to generate a new layout or 'Delete Game' to clear all board data entirely.",
    },
    {
      title: "Save/Randomize Board",
      level: 3,
      parentId: 30,
      image:
        "/assets/guide/auth/profile/clan/events/manage_game/save_and_randomize_board_confirmation.png",
      description:
        "Save & Randomize. Finalize your board settings and trigger a fresh randomization of hazard locations.",
    },
    {
      title: "Wipe Board Confirmation",
      level: 3,
      parentId: 30,
      image:
        "/assets/guide/auth/profile/clan/events/manage_game/wipe_board_confirmation.png",
      description:
        "Delete game. Permanently erase the entire board configuration, including all custom task layouts, snakes, and ladders. This action cannot be undone and will return you to the initial game setup form.",
    },
    { title: "Game Tasks Management", level: 0, isHeader: true, id: 50 },
    {
      title: "Tasks Overview",
      level: 1,
      parentId: 50,
      image:
        "/assets/guide/auth/profile/clan/events/manage_game_tasks/manage_game_tasks.png",
      description:
        "Game Tasks Management. Owners and admins can oversee all available tasks, reorder them via drag-and-drop, or edit individual task definitions.",
    },
    {
      title: "Edit Task Modal",
      level: 1,
      parentId: 50,
      image:
        "/assets/guide/auth/profile/clan/events/manage_game_tasks/edit_task_modal.png",
      description:
        "Edit task details. Use this modal to swap an existing task for a different one within the same difficulty tier, ensuring your board challenges remain balanced and engaging.",
    },
    {
      title: "Reorder Tasks",
      level: 1,
      parentId: 50,
      image:
        "/assets/guide/auth/profile/clan/events/manage_game_tasks/drag_n_drop_tasks_reordering.png",
      description:
        "Organize your task list. Use drag-and-drop to reorder tasks within their specific difficulty tier, allowing you to prioritize the order of challenges presented on the board.",
    },
  ];

  const isVisible = (step) => {
    if (step.level === 0) return true;
    const parent = steps.find((s) => s.id === step.parentId);
    return parent
      ? expandedSections[step.parentId] && isVisible(parent)
      : false;
  };

  const toggleSection = (id) =>
    setExpandedSections((prev) => ({ ...prev, [id]: !prev[id] }));

  const navigate = (direction) => {
    let nextIndex = stepIndex + direction;
    while (nextIndex >= 0 && nextIndex < steps.length) {
      if (!steps[nextIndex].isHeader) {
        setStepIndex(nextIndex);
        let current = steps[nextIndex];
        let pathIds = [];
        while (current.parentId) {
          pathIds.push(current.parentId);
          current = steps.find((s) => s.id === current.parentId);
        }
        pathIds.push(current.id);
        const newExpanded = {
          0: false,
          1: false,
          2: false,
          6: false,
          7: false,
          10: false,
          11: false,
          12: false,
          20: false,
          25: false,
          30: false,
          40: false,
          50: false,
        };
        pathIds.forEach((id) => (newExpanded[id] = true));
        setExpandedSections(newExpanded);
        return;
      }
      nextIndex += direction;
    }
  };

  return (
    <div className="guide-layout">
      <nav className="sub-nav">
        {steps.map((step, idx) => {
          const show = isVisible(step);
          const isHeader = !!step.isHeader;
          return (
            <div
              key={idx}
              className={`nav-item-wrapper ${stepIndex === idx ? "active" : ""}`}
              style={{
                maxHeight: show ? "60px" : "0px",
                opacity: show ? 1 : 0,
              }}
            >
              <span
                className={`sub-nav-item level-${step.level} ${isHeader ? "sub-nav-header" : ""}`}
                onClick={() =>
                  isHeader ? toggleSection(step.id) : setStepIndex(idx)
                }
              >
                {isHeader && (
                  <span
                    style={{
                      marginRight: "8px",
                      transform: expandedSections[step.id]
                        ? "rotate(90deg)"
                        : "rotate(0deg)",
                      transition: "transform 0.2s",
                    }}
                  >
                    <ChevronRight size={16} />
                  </span>
                )}
                {step.title}
              </span>
            </div>
          );
        })}
      </nav>
      <main className="guide-main">
        <div className="guide-item">
          <div className="guide-text">
            <h2>{steps[stepIndex].title}</h2>
            <div className="nav-controls">
              <button disabled={stepIndex === 0} onClick={() => navigate(-1)}>
                Back
              </button>
              <button
                disabled={stepIndex === steps.length - 1}
                onClick={() => navigate(1)}
                style={{ marginLeft: "10px" }}
              >
                {stepIndex === steps.length - 1 ? "Finish" : "Next"}
              </button>
            </div>
            {!steps[stepIndex].isHeader && (
              <div className="guide-text-wrapper">
                <p className="step-description">
                  {steps[stepIndex].description}
                </p>
              </div>
            )}
          </div>
          {!steps[stepIndex].isHeader && (
            <div
              className={`screenshot-container ${steps[stepIndex].title === "Tasks Overview" ? "tall-image-container" : ""}`}
            >
              <img
                src={steps[stepIndex].image}
                alt={steps[stepIndex].title}
                className={
                  steps[stepIndex].title === "Tasks Overview"
                    ? "tall-image"
                    : ""
                }
              />
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default ClanManagementSection;
